// filepath: src/pages/ProductList.js
import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
// import tinycolor from "tinycolor2";

import SearchInput from "../components/CommonComponents/SearchInput";
import ReactSelectField from "../components/CommonComponents/ReactSelectField";
import DeleteModal from "../components/CommonComponents/ConfirmationModal";
import AddProductModal from "../components/admin/products/AddProductModal";
import ProductGrid from "../components/admin/products/ProductGrid";
import ProductTable from "../components/admin/products/ProductTable";
import ProductImagePreviewModal from "../components/admin/products/ProductImagePreviewModal";
import ViewToggle from "../components/CommonComponents/ViewToggle";
import EmptyState from "../components/CommonComponents/EmptyState";
import { showSuccessToast, showErrorToast } from "../utils/toast";
import { Boxes, ChevronDown, Package, Search, Sparkles } from "lucide-react";
import PrimaryButton from "../components/CommonComponents/PrimaryButton";
import { authStorage } from "../utils/authStorage";
import { resolveApiUrl } from "../utils/apiBaseUrl";
import { PRODUCT_CATEGORY_FILTER_OPTIONS } from "../constants/productCategories";

const ProductListSkeleton = ({ viewMode = "table" }) => (
    <div className="animate-pulse">
        {viewMode === "table" ? (
            <div className="overflow-x-auto rounded-[28px] border border-slate-200/80 bg-white shadow-[0_18px_45px_rgba(15,23,42,0.06)]">
                <div className="min-w-[1080px] lg:min-w-[980px] xl:min-w-0">
                    <div
                        className="grid grid-cols-[minmax(260px,2.4fr),minmax(130px,1fr),minmax(120px,0.9fr),minmax(90px,0.7fr),minmax(130px,1fr),minmax(130px,1fr),minmax(110px,0.8fr)] items-center gap-4 rounded-t-[28px] border-b px-4 py-3 xl:px-5"
                        style={{
                            background: "linear-gradient(180deg, var(--color-primary), color-mix(in srgb, var(--color-primary) 82%, black))",
                            borderColor: "color-mix(in srgb, var(--color-primary) 72%, black)",
                        }}
                    >
                        {Array.from({ length: 7 }).map((_, idx) => (
                            <div key={idx} className={`h-3 rounded-full bg-white/25 ${idx === 0 ? "w-16" : "mx-auto w-20"}`} />
                        ))}
                    </div>
                    <div className="divide-y divide-slate-100">
                        {Array.from({ length: 8 }).map((_, rowIdx) => (
                            <div
                                key={rowIdx}
                                className="grid grid-cols-[minmax(260px,2.4fr),minmax(130px,1fr),minmax(120px,0.9fr),minmax(90px,0.7fr),minmax(130px,1fr),minmax(130px,1fr),minmax(110px,0.8fr)] items-center gap-4 bg-slate-50/85 px-4 py-2.5 even:bg-white xl:px-5"
                            >
                                <div className="flex items-center gap-2.5">
                                    <div className="h-9 w-9 rounded-xl bg-slate-200" />
                                    <div className="min-w-0 flex-1 space-y-2">
                                        <div className="h-3.5 w-40 rounded-full bg-slate-200" />
                                        <div className="h-2.5 w-56 max-w-full rounded-full bg-slate-100" />
                                    </div>
                                </div>
                                <div className="mx-auto h-7 w-32 rounded-full border border-slate-100 bg-white shadow-sm" />
                                <div className="mx-auto h-7 w-24 rounded-2xl border border-emerald-100 bg-emerald-50" />
                                <div className="mx-auto h-6 w-14 rounded-full border border-slate-100 bg-white shadow-sm" />
                                <div className="mx-auto h-7 w-28 rounded-2xl border border-slate-100 bg-slate-50" />
                                <div className="mx-auto h-7 w-28 rounded-2xl border border-slate-100 bg-slate-50" />
                                <div className="flex justify-center gap-2">
                                    <div className="h-9 w-9 rounded-xl border border-slate-100 bg-white shadow-sm" />
                                    <div className="h-9 w-9 rounded-xl border border-rose-100 bg-rose-50" />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
                {Array.from({ length: 8 }).map((_, idx) => (
                    <div key={idx} className="overflow-hidden rounded-[26px] border border-slate-200 bg-white/95 p-4 shadow-[0_18px_40px_rgba(15,23,42,0.07)]">
                        <div className="relative h-40 rounded-2xl bg-gradient-to-r from-slate-100 via-slate-200 to-slate-100">
                            <div className="absolute left-3 top-3 h-7 w-16 rounded-full bg-white/80" />
                        </div>
                        <div className="mt-4 space-y-2">
                            <div className="h-4 w-4/5 rounded-full bg-slate-200" />
                            <div className="h-3 w-1/2 rounded-full bg-slate-100" />
                            <div className="h-6 rounded-full bg-emerald-50" />
                            <div className="h-6 rounded-full bg-slate-100" />
                        </div>
                        <div className="mt-4 flex justify-end gap-2">
                            <div className="h-9 w-9 rounded-xl bg-slate-100" />
                            <div className="h-9 w-9 rounded-xl bg-rose-50" />
                        </div>
                    </div>
                ))}
            </div>
        )}
    </div>
);

const ProductList = () => {
    const storedUser = authStorage.getUser();
    const isManager = storedUser?.user_type === 2
        && String(storedUser?.role_name || "").toLowerCase() === "manager";
    const canManageProducts = !isManager;
    // ...existing code...
    // -------- State --------
    const [products, setProducts] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [categoryFilter, setCategoryFilter] = useState("All");
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalProducts, setTotalProducts] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [modal, setModal] = useState({ type: null, product: null, open: false });
    const [previewProduct, setPreviewProduct] = useState(null);
    const [viewMode, setViewMode] = useState("table"); // "table" or "grid"
    const [showSummaryCards, setShowSummaryCards] = useState(true);
    const productsPerPage = 10;

    const baseURL = `${process.env.REACT_APP_API_URL}/api/admin/products`;
    const uploadsBaseURL = resolveApiUrl("/uploads");

    // ---------------- Axios Auth Helper ----------------
    const getAuthHeaders = () => {
        const token = sessionStorage.getItem("token") || localStorage.getItem("token");
        if (!token) throw new Error("Authorization token missing.");
        return { Authorization: `Bearer ${token}` };
    };

    // -------- Fetch Products --------
    const loadProducts = useCallback(async (page = 1, search = "", category = "All") => {
        setLoading(true);
        setError(null);
        try {
            const params = { page, limit: productsPerPage };
            if (search) params.search = search;
            if (category && category !== "All") params.category = category;
            const res = await axios.get(baseURL, { headers: getAuthHeaders(), params });
            setProducts(res.data.products || []);
            setTotalPages(res.data.totalPages || 1);
            setTotalProducts(res.data.total || 0);
        } catch (err) {
            console.error(err);
            setError(err);
        } finally {
            setLoading(false);
        }
    }, [baseURL, productsPerPage]);

    useEffect(() => {
        loadProducts(currentPage, searchTerm, categoryFilter);
    }, [loadProducts, currentPage, searchTerm, categoryFilter]);

    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(1);
        }
    }, [currentPage, totalPages]);

    // ---------------- Pagination Handlers ----------------
    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };

    const handleCategoryChange = (option) => {
        setCategoryFilter(option?.value || "All");
        setCurrentPage(1);
    };

    const productSuggestions = useMemo(() => {
        const normalizedSearch = searchTerm.trim().toLowerCase();
        if (!normalizedSearch) return [];
        const uniqueNames = new Set();

        return products
            .map((product) => product.name || product.product_name || "")
            .filter((name) => {
                if (!name || uniqueNames.has(name)) return false;
                const matches = name.toLowerCase().includes(normalizedSearch);
                if (matches) {
                    uniqueNames.add(name);
                }
                return matches;
            })
            .slice(0, 6);
    }, [products, searchTerm]);

    const safePage = Math.min(currentPage, totalPages);
    const startIndex = (safePage - 1) * productsPerPage;
    const selectedCategoryOption = PRODUCT_CATEGORY_FILTER_OPTIONS.find((option) => option.value === categoryFilter)
        || PRODUCT_CATEGORY_FILTER_OPTIONS[0];
    const summaryCards = [
        {
            label: "Products",
            value: totalProducts,
            hint: "Total catalog items",
            icon: Package,
        },
        {
            label: "Visible",
            value: products.length,
            hint: "Items in the current page",
            icon: Boxes,
        },
        {
            label: "Search Scope",
            value: searchTerm ? "Filtered" : "All",
            hint: searchTerm ? `Results for "${searchTerm}"` : "No active search filter",
            icon: Search,
        },
        {
            label: "Category",
            value: categoryFilter === "All" ? "All" : categoryFilter,
            hint: categoryFilter === "All" ? "All categories visible" : "Active category filter",
            icon: Sparkles,
        },
    ];


    // ---------------- Modal Helpers ----------------
    const openModal = (type, product = null) =>
        setModal({ type, product, open: true });
    const closeModal = () => setModal({ type: null, product: null, open: false });

    // ---------------- Delete Product ----------------
    const handleDeleteProduct = async () => {
        if (!modal.product) return;
        try {
            await axios.delete(`${baseURL}/${modal.product.id}`, { headers: getAuthHeaders() });
            setProducts((prev) => prev.filter((p) => p.id !== modal.product.id));
            closeModal();
            showSuccessToast("Product deleted successfully.");
        } catch (err) {
            console.error(err);
            showErrorToast(err.response?.data?.message || "Failed to delete product.");
        }
    };

    // ---------------- Add / Edit Product ----------------
    const handleAddEditProduct = async (formData, isEdit = false) => {
        const url = isEdit ? `${baseURL}/${modal.product.id}` : baseURL;
        try {
            const res = isEdit
                ? await axios.put(url, formData, {
                    headers: getAuthHeaders(),
                })
                : await axios.post(url, formData, {
                    headers: getAuthHeaders(),
                });

            showSuccessToast(res.data.message || (isEdit ? "Product updated" : "Product added"));
            loadProducts(currentPage, searchTerm, categoryFilter);
            closeModal();
        } catch (err) {
            console.error(err);
            showErrorToast(err.response?.data?.message || (isEdit ? "Failed to update" : "Failed to add"));
        }
    };

    // ---------------- Render ----------------
    return (
        <div className="pt-3">
            <div className="flex flex-col gap-4">
                <section className="relative overflow-hidden rounded-[30px] border border-slate-200/85 bg-[linear-gradient(145deg,#fffafa,#fffdfd,#ffffff)] p-5 shadow-[0_24px_80px_rgba(15,23,42,0.08)] sm:p-6">
                    <div className="pointer-events-none absolute inset-0">
                        <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full blur-3xl" style={{ background: "color-mix(in srgb, var(--color-primary) 18%, transparent)" }} />
                        <div className="absolute left-10 top-0 h-28 w-28 rounded-full bg-white/60 blur-2xl" />
                    </div>

                    <div className="relative flex flex-col gap-4">
                        <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
                            <div className="min-w-0">
                                <div className="inline-flex items-center gap-2 rounded-full border border-white/70 bg-white/80 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500 shadow-sm">
                                    <Package className="h-3.5 w-3.5" />
                                    Products Workspace
                                </div>
                                <h1 className="mt-4 text-3xl font-semibold tracking-tight text-slate-950 sm:text-4xl">
                                    Products List
                                </h1>
                                <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500 sm:text-[15px]">
                                    Manage your product catalog with a streamlined workspace for searching, browsing, and organizing products.
                                </p>
                            </div>
                        </div>

                        <div className="flex items-center justify-between gap-3 rounded-[22px] border border-white/70 bg-white/80 px-4 py-3 shadow-[0_8px_18px_rgba(15,23,42,0.06)]">
                            <div>
                                <p className="text-sm font-semibold text-slate-900">Products Summary</p>
                                <p className="mt-1 text-xs text-slate-500">Show or hide the Products, Visible, Search Scope, and Category cards.</p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setShowSummaryCards((prev) => !prev)}
                                className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-600 shadow-sm transition hover:bg-slate-50"
                                aria-expanded={showSummaryCards}
                                aria-label={showSummaryCards ? "Hide product summary cards" : "Show product summary cards"}
                            >
                                {showSummaryCards ? "Hide" : "Show"}
                                <ChevronDown className={`h-4 w-4 transition ${showSummaryCards ? "rotate-180" : "rotate-0"}`} />
                            </button>
                        </div>

                        {showSummaryCards && (
                            <div className="grid grid-cols-2 gap-2 xl:grid-cols-4">
                                {summaryCards.map(({ label, value, hint, icon: Icon }) => (
                                    <div key={label} className="rounded-[24px] border border-white/70 bg-white/80 p-4 shadow-[0_8px_18px_rgba(15,23,42,0.06)]">
                                        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                                            <Icon className="h-4 w-4" />
                                            {label}
                                        </div>
                                        <p className="mt-3 truncate text-2xl font-semibold text-slate-900">{value}</p>
                                        <p className="mt-1 truncate text-xs text-slate-500">{hint}</p>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </section>

                <section className="sticky z-30 rounded-[30px] border border-slate-200/80 bg-white/95 p-4 shadow-[0_18px_60px_rgba(15,23,42,0.09)] backdrop-blur-md" style={{ top: "var(--admin-sticky-offset, 0px)" }}>
                    <div className="grid min-w-0 gap-4 xl:grid-cols-[minmax(220px,0.8fr),minmax(0,1.7fr),auto] xl:items-center">
                        <div className="min-w-0">
                            <p className="text-sm font-semibold text-slate-900">Catalog Layout</p>
                            <p className="mt-1 max-w-md text-xs leading-5 text-slate-500">
                                Browse products by category in table or grid view while keeping the overall page structure consistent with inventory.
                            </p>
                        </div>
                        <div className="grid min-w-0 gap-3 md:grid-cols-[minmax(210px,240px),minmax(280px,1fr)]">
                            <div className="min-w-0">
                                <ReactSelectField
                                    options={PRODUCT_CATEGORY_FILTER_OPTIONS}
                                    value={selectedCategoryOption}
                                    onChange={handleCategoryChange}
                                    placeholder="All Categories"
                                    isSearchable
                                    ariaLabel="Filter products by category"
                                />
                            </div>
                            <div className="min-w-0">
                                <SearchInput
                                    value={searchTerm}
                                    onChange={handleSearchChange}
                                    onSuggestionSelect={(productName) => {
                                        setSearchTerm(productName);
                                        setCurrentPage(1);
                                    }}
                                    suggestions={productSuggestions}
                                    placeholder="Search by product name or barcode..."
                                    className="group relative w-full"
                                />
                            </div>
                        </div>
                        <div className="grid min-w-0 grid-cols-[minmax(0,1fr),44px] items-center gap-2 sm:flex sm:shrink-0 sm:justify-end sm:gap-3">
                            {canManageProducts && (
                                <PrimaryButton
                                    className="min-w-0 max-w-none truncate rounded-xl px-3 py-2 text-xs sm:w-auto sm:shrink-0 sm:px-4 sm:text-sm md:px-5"
                                    onClick={() => openModal("add")}
                                >
                                    + Add Product
                                </PrimaryButton>
                            )}
                            <ViewToggle
                                viewMode={viewMode}
                                onToggle={() => setViewMode(viewMode === "table" ? "grid" : "table")}
                                className="justify-self-end"
                            />
                        </div>
                    </div>
                </section>

                <section className="p-0">
                    {error ? (
                        <EmptyState
                            icon={Package}
                            title="Failed to Load Products"
                            message="Check your internet connection and try again."
                            onRetry={() => loadProducts(currentPage, searchTerm, categoryFilter)}
                            retryLabel="Reload"
                        />
                    ) : loading ? (
                        <ProductListSkeleton viewMode={viewMode} />
                    ) : products.length === 0 ? (
                        <EmptyState
                            icon={Package}
                            title="No Products Found"
                            message={searchTerm || categoryFilter !== "All" ? "No products match your current filters." : "No products available yet."}
                        />
                    ) : viewMode === "table" ? (
                        <ProductTable
                            products={products}
                            onEdit={canManageProducts ? (p) => openModal("edit", p) : undefined}
                            onDelete={canManageProducts ? (p) => openModal("delete", p) : undefined}
                            onPreviewImage={setPreviewProduct}
                            uploadsBaseURL={uploadsBaseURL}
                            showActions={canManageProducts}
                        />
                    ) : (
                        <ProductGrid
                            products={products}
                            onEdit={canManageProducts ? (p) => openModal("edit", p) : undefined}
                            onDelete={canManageProducts ? (p) => openModal("delete", p) : undefined}
                            onPreviewImage={setPreviewProduct}
                            uploadsBaseURL={uploadsBaseURL}
                            showActions={canManageProducts}
                        />
                    )}
                </section>

                {!error && (
                    <div className="flex items-center justify-between gap-3 overflow-x-auto pb-10 text-xs whitespace-nowrap">
                        <div className="shrink-0 text-gray-500">
                            Showing {products.length === 0 ? 0 : startIndex + 1}
                            -{Math.min(startIndex + productsPerPage, totalProducts)} of {totalProducts}
                        </div>
                        <div className="flex shrink-0 items-center gap-2">
                            <button
                                type="button"
                                className="px-3 py-1.5 rounded-lg border text-xs disabled:opacity-50"
                                onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                                disabled={safePage === 1}
                            >
                                Prev
                            </button>
                            <div className="text-xs text-gray-600">
                                Page {safePage} of {totalPages}
                            </div>
                            <button
                                type="button"
                                className="px-3 py-1.5 rounded-lg border text-xs disabled:opacity-50"
                                onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                                disabled={safePage === totalPages}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Add / Edit / Delete Modals */}
            {modal.open && modal.type === "add" && (
                <AddProductModal editData={null} onClose={closeModal} onSubmit={handleAddEditProduct} isOpen />
            )}
            {modal.open && modal.type === "edit" && (
                <AddProductModal editData={modal.product} onClose={closeModal} onSubmit={(data) => handleAddEditProduct(data, true)} isOpen />
            )}
            {modal.open && modal.type === "delete" && (
                <DeleteModal
                    isOpen
                    onClose={closeModal}
                    onConfirm={handleDeleteProduct}
                    title="Delete Product"
                    message={`Are you sure you want to delete "${modal.product?.name}"?`}
                    confirmText="Yes"
                    cancelText="No"
                    type="delete"
                />
            )}
            <ProductImagePreviewModal
                open={Boolean(previewProduct)}
                onClose={() => setPreviewProduct(null)}
                product={previewProduct}
            />
        </div>
    );
};

export default ProductList;
